// Texture preset table — resolves a wire-level `TexturePreset` id to the
// concrete `TextureSampler` the kernel uses (texture-brush-design.md §1 table
// / §4 resolve / §11 step 2).
//
// The protocol carries only the preset id (`paper` / `bristle` / `spray` /
// `cloud` / `hatch`); the render side owns the mapping mode, the asset bytes,
// and the penWidth-following `scale`. This table is that ownership boundary —
// both the web `EngineCore` and the server `SnapshotEngine` resolve a preset
// through here, so they pick the SAME sampler → byte-identical strokes.
//
// Per-preset mapping (texture-brush-design.md §1):
//   | preset  | mapping | rotate-with-stroke | notes                          |
//   |---------|---------|--------------------|--------------------------------|
//   | paper   | world   | n/a                | 紙目, 下地露出 (spike)          |
//   | hatch   | world   | n/a                | 規則網点, 揃える               |
//   | bristle | stamp   | yes                | 毛筋が筆運びに沿う (方向性)    |
//   | spray   | world   | n/a                | 型紙ステンシル. world 確定 ↓   |
//   | cloud   | world   | n/a                | 不定形の霧. world 確定 ↓       |
//
// world 確定 (§1 要検証 → 解決, 2026-06-19): spray / cloud は当初 stamp だった
// が、diffuse 系を stamp にすると各 dab が dab 中心相対で texel を引くため、密に
// 重ねると同じ canvas 画素が dab ごとに別 texel を引き、穴が隣 dab の高 alpha で
// 埋まって source-over でベタ = 素の brush に見える ("霧" バグ)。world は texel を
// canvas 座標に固定するので穴がどの dab からも穴のまま残り、テクスチャが消えない。
// 方向性のある bristle のみ stamp 据え置き (毛筋が筆運びに沿う本質を保つ)。
//
// `rotateWithStroke` is implicit in `mapping === 'stamp'` (the kernel rotates
// every stamp-mapped texture by the stroke direction; world never rotates).
// We keep the field on `TextureSampler` minimal: `mapping` + `scale` carry
// everything the kernel needs.
import { PAPER_TEXTURE_SAMPLER } from './paperTexture.js';
import { BRISTLE_TEXTURE_SAMPLER, CLOUD_TEXTURE_SAMPLER, HATCH_TEXTURE_SAMPLER, SPRAY_TEXTURE_SAMPLER, } from './presetTextures.js';
/**
 * Preset id → sampler. Frozen so no caller can mutate the shared instances
 * (they're immutable assets baked at module load).
 */
export const TEXTURE_PRESET_TABLE = Object.freeze({
    paper: PAPER_TEXTURE_SAMPLER,
    bristle: BRISTLE_TEXTURE_SAMPLER,
    spray: SPRAY_TEXTURE_SAMPLER,
    cloud: CLOUD_TEXTURE_SAMPLER,
    hatch: HATCH_TEXTURE_SAMPLER,
    // スクリーントーン was here as `tone-dot-*` static presets; it is now its own
    // `pen='tone'` (threshold dither, texture-brush-design.md §12.2).
});
/**
 * The spike default: `pen='texture'` without `penParams` renders as「紙」
 * (texture-brush-design.md §4). Keep this stable — already-recorded texture
 * strokes that omit a preset must keep replaying as paper.
 */
export const DEFAULT_TEXTURE_PRESET = 'paper';
/**
 * Stable preset ordering → numeric index. Matches the protocol
 * `texturePresetSchema` enum order. The Wasm texture ABI passes this small
 * integer (NOT the 64 KB alpha array) on every `begin_stroke`; the array is
 * registered once at boot keyed by the same index (texture-brush-design.md §11
 * — "毎 stroke で 64KB をコピーしない"). MUST stay append-only so already-baked
 * snapshots / late-join replays keep resolving the same preset.
 *
 * NOTE: the 網点 (screentone) presets were removed from this list when トーン
 * was promoted to `pen='tone'` (threshold dither, §12.2). The tone threshold
 * map registers at its own fixed registry index past the texture presets
 * (`TONE_TEXTURE_INDEX_BASE`), so this list stays exactly the 4 出荷 texture
 * presets.
 */
export const TEXTURE_PRESET_ORDER = Object.freeze([
    'paper',
    'bristle',
    'spray',
    'cloud',
    'hatch',
]);
/**
 * Wasm registry index BASE for the `pen='tone'` clustered-dot threshold map
 * FAMILY (`toneThreshold.ts`'s `TONE_THRESHOLD_SAMPLERS`, one per
 * `TONE_PITCHES` entry, phase11-light-pens-2026-08-21.md §4.5). Registers at
 * `TONE_TEXTURE_INDEX_BASE + pitchIndex` (0..`TONE_PITCHES.length - 1`), i.e.
 * indices 16..20 today (5 pitches) — placed past every texture preset so it
 * never collides with `TEXTURE_PRESET_ORDER`. Both the JS `registerTextures()`
 * and the Rust `Texture::from_preset` resolve a tone map at one of these
 * indices, so the threshold bytes are byte-identical across JS / Wasm (parity
 * ABSOLUTE).
 *
 * **Not append-only across the pitch/grain families** (unlike
 * `TEXTURE_PRESET_ORDER`'s own append-only contract): this is a purely
 * in-process Wasm module registry key, assigned fresh every worker boot and
 * NEVER carried on the wire or persisted in a snapshot — so renumbering it
 * (as happened here, shifting `CRAYON_TEXTURE_INDEX_BASE` /
 * `PASTEL_TEXTURE_INDEX_BASE` down when tone grew from 1 slot to 5) cannot
 * break replay. If tone grows a 6th pitch, extend `TONE_PITCHES` and shift
 * `CRAYON_TEXTURE_INDEX_BASE` / `PASTEL_TEXTURE_INDEX_BASE` down again —
 * cheap, since nothing durable references these numbers.
 */
export const TONE_TEXTURE_INDEX_BASE = 16;
/**
 * Wasm registry index BASE for `pen='crayon'`'s 3 grain-level threshold maps
 * (`crayonThreshold.ts`, phase11-light-pens-2026-08-21.md §2.5/§5). Registers
 * at `CRAYON_TEXTURE_INDEX_BASE + grainIndex` (0..2), i.e. indices 21..23 —
 * strictly past every `TONE_TEXTURE_INDEX_BASE + pitchIndex` slot (see that
 * constant's "not append-only" doc for why renumbering here is safe).
 */
export const CRAYON_TEXTURE_INDEX_BASE = 21;
/**
 * Wasm registry index BASE for `pen='pastel'`'s 3 grain-level alpha maps
 * (`pastelTexture.ts`). Registers at `PASTEL_TEXTURE_INDEX_BASE + grainIndex`
 * (0..2), i.e. indices 24..26 — past crayon's 3-slot range.
 */
export const PASTEL_TEXTURE_INDEX_BASE = 24;
/** Preset id → its registry index (= position in `TEXTURE_PRESET_ORDER`). */
export const texturePresetIndex = (preset) => TEXTURE_PRESET_ORDER.indexOf(preset);
/** Pitch index (0..4, `tonePitchIndex`'s output) → Wasm registry index. */
export const tonePitchTextureIndex = (pitchIndex) => TONE_TEXTURE_INDEX_BASE + pitchIndex;
/** Grain index (0..2, `crayonGrainIndex`'s output) → Wasm registry index. */
export const crayonTextureIndex = (grainIndex) => CRAYON_TEXTURE_INDEX_BASE + grainIndex;
/** Grain index (0..2, `pastelGrainIndex`'s output) → Wasm registry index. */
export const pastelTextureIndex = (grainIndex) => PASTEL_TEXTURE_INDEX_BASE + grainIndex;
/**
 * Resolve a preset id (or `undefined` = spike default) to its sampler.
 * Used by `createTextureStampRenderer` and any UI thumbnail path.
 */
export const samplerForPreset = (preset) => TEXTURE_PRESET_TABLE[preset ?? DEFAULT_TEXTURE_PRESET];
//# sourceMappingURL=presetTable.js.map